import React from "react";
import Button from "../../../ui/button";
import FieldGroup from "../../../ui/field-group";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Select } from "../../../ui/select";
import { Textarea } from "../../../ui/textarea";
import { DateTimeInput } from "../../../ui/datetime-input";
import { DateInput } from "../../../ui/date-input";
import styles from "./styles.module.scss";
import type { FormProps } from "../types";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { BeatLoader } from "react-spinners";
import { saveFormInput, getFormInput } from "../../../../libs/fns";
import { createZodSchemaForStep } from "../fns";
import MobilePicker from "../../../ui/mobile-picker";
import { useBottomSheet } from "../../../../contexts/bottom-sheet";

type Props = {
  formProps: FormProps;
  callback: (values: Record<string, unknown>) => Promise<void>;
};

// pickerフィールドの保存値("YYYY-MM-DD"または"YYYY-MM-DD HH:MM")を
// MobilePicker/Controllerが期待する{year,month,day[,hour,minute]}形式へ復元する。
// createZodSchemaForStep()のtransformでこの文字列形式に変換されて保存されるため、
// JSON.parse()では復元できない（非JSON文字列としてパース失敗し、生の文字列が
// そのままpicker値になってしまい "undefined年undefined月undefined日" 表示や
// 選択済み日付の消失を引き起こしていた）。
export function parseSavedPickerValue(savedValue: string): Record<string, string> | string {
  const withTimeMatch = savedValue.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/);
  if (withTimeMatch) {
    const [, year, month, day, hour, minute] = withTimeMatch;
    return { year, month, day, hour, minute };
  }
  const dateOnlyMatch = savedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return { year, month, day };
  }
  // 後方互換: 過去にJSON文字列として保存されていた場合のフォールバック
  try {
    return JSON.parse(savedValue);
  } catch {
    return savedValue;
  }
}

// ステップごとに完全に独立したフォームコンポーネント
// stepが変更されるたびに完全に新しいインスタンスが作成される
function StepFormInner({
  step,
  formProps,
  onSubmit,
  onPrev,
  isLastStep,
}: {
  step: number;
  formProps: FormProps;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  onPrev: () => void;
  isLastStep: boolean;
}) {
  // 現在のstepのフィールドを取得（マウント時に一度だけ計算）
  const currentFields = formProps.page[step]?.list.flatMap((listItem) => listItem.fields) || [];

  const schema = React.useMemo(
    () => createZodSchemaForStep(currentFields),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // マウント時に一度だけ計算、key propでリマウントされるため安全
  );

  // sessionStorageから保存された値を取得してデフォルト値として設定
  const defaultValues = React.useMemo(() => {
    const values: Record<string, any> = {};
    currentFields.forEach((field) => {
      const savedValue = getFormInput(field.name);
      if (savedValue) {
        if (field.type === "picker") {
          values[field.name] = parseSavedPickerValue(savedValue);
        } else {
          values[field.name] = savedValue;
        }
      }
    });
    return values;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // マウント時に一度だけ実行

  const {
    register,
    handleSubmit,
    control,
    formState: { isValid, isSubmitting, errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "all",
    defaultValues,
  });

  // 値が変更されたらsessionStorageに保存
  // watch()を使わず、フォーム送信時にのみ保存するように変更
  const handleFormSubmitWithSave = async (data: Record<string, unknown>) => {
    // データを保存
    Object.keys(data).forEach((key) => {
      const value = data[key];
      if (value !== undefined && value !== null && value !== "") {
        const stringValue =
          typeof value === "object" ? JSON.stringify(value) : String(value);
        saveFormInput(key, stringValue);
      }
    });
    // 親コンポーネントのonSubmitを呼び出し
    await onSubmit(data);
  };

  // ページが空の場合は何も表示しない
  if (!formProps.page || formProps.page.length === 0 || !formProps.page[step]) {
    console.error('[StepFormInner] ページデータが存在しません:', { step, pageLength: formProps.page?.length });
    return <div className={styles.form}>フォームデータが見つかりません</div>;
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit(handleFormSubmitWithSave)}>
      <div className={styles.step}>
        <span className={styles.stepCurrent}>{step + 1}</span>/
        <span className={styles.stepTotal}>{formProps.page.length}</span>
      </div>
      <div className={styles.body}>
        {step === 0 && formProps.title && (
          <p className={styles.lead}>{formProps.title}</p>
        )}
        {formProps.page[step]?.list && (
          <div className={styles.fieldList}>
            {formProps.page[step].list.map((item) => (
              <FieldGroup
                key={item.form_index}
                index={item.form_index}
                heading={item.form_name}
                required={item.required}
              >
                {item.fields.map((field) => (
                  <div key={field.name} className={styles.field}>
                    {field.label && (
                      <Label htmlFor={field.name}>{field.label}</Label>
                    )}
                    {field.type === "select" ? (
                      <div>
                        <Select
                          id={field.name}
                          {...register(field.name)}
                          error={errors[field.name] && true}
                        >
                          {field.options?.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.value}
                            </option>
                          ))}
                        </Select>
                        {errors[field.name] && (
                          <p className={styles.error}>
                            {String(errors[field.name]?.message)}
                          </p>
                        )}
                      </div>
                    ) : field.type === "textarea" ? (
                      <div>
                        <Textarea
                          id={field.name}
                          {...register(field.name)}
                          placeholder={field.placeholder}
                          error={errors[field.name] && true}
                        />
                        {errors[field.name] && (
                          <p className={styles.error}>
                            {String(errors[field.name]?.message)}
                          </p>
                        )}
                      </div>
                    ) : field.type === "picker" ? (
                      <div>
                        <Controller
                          name={field.name}
                          control={control}
                          defaultValue={
                            field.includeTime
                              ? {
                                year: field.start
                                  ? `${field.start}`
                                  : `${new Date().getFullYear()}`,
                                month: "01",
                                day: "01",
                                hour: "00",
                                minute: "00",
                              }
                              : {
                                year: field.start
                                  ? `${field.start}`
                                  : `${new Date().getFullYear()}`,
                                month: "01",
                                day: "01",
                              }
                          }
                          render={({ field: { onChange, value } }) => (
                            <MobilePicker
                              onChange={onChange}
                              value={value}
                              error={errors[field.name] && true}
                              start={field.start}
                              length={field.length}
                              calc={field.calc}
                              includeTime={field.includeTime}
                            />
                          )}
                        />
                        {errors[field.name] && (
                          <p className={styles.error}>
                            {String(errors[field.name]?.message)}
                          </p>
                        )}
                      </div>
                    ) : field.type === "date" ? (
                      <div>
                        <Controller
                          name={field.name}
                          control={control}
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <DateInput
                              id={field.name}
                              onChange={onChange}
                              value={value as string}
                              placeholder={field.placeholder}
                              error={errors[field.name] && true}
                            />
                          )}
                        />
                        {errors[field.name] && (
                          <p className={styles.error}>
                            {String(errors[field.name]?.message)}
                          </p>
                        )}
                      </div>
                    ) : field.type === "datetime-local" ? (
                      <div>
                        <Controller
                          name={field.name}
                          control={control}
                          defaultValue=""
                          render={({ field: { onChange, value } }) => (
                            <DateTimeInput
                              id={field.name}
                              onChange={onChange}
                              value={value as string}
                              placeholder={field.placeholder}
                              error={errors[field.name] && true}
                            />
                          )}
                        />
                        {errors[field.name] && (
                          <p className={styles.error}>
                            {String(errors[field.name]?.message)}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <Input
                          id={field.name}
                          {...register(field.name)}
                          type={field.type}
                          placeholder={field.placeholder}
                          error={errors[field.name] && true}
                        />
                        {errors[field.name] && (
                          <p className={styles.error}>
                            {String(errors[field.name]?.message)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </FieldGroup>
            ))}
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <Button type="button" variant="outline" size="sm" onClick={onPrev}>
          戻る
        </Button>
        <Button size="sm" type="submit" disabled={!isValid || isSubmitting}>
          {isLastStep ? "送信" : "次へ"}
          {isSubmitting && (
            <span className={styles.loader} data-testid="isPending">
              <BeatLoader size={8} color="#ffffff" />
            </span>
          )}
        </Button>
      </div>
    </form>
  );
}

export default function DynamicMultiStepForm({ formProps, callback }: Props) {
  const { openBottomSheet } = useBottomSheet();
  const [step, setStep] = React.useState(0);

  // 空のページを除外したformPropsを作成
  const filteredFormProps = React.useMemo(() => {
    const filteredPages = formProps.page.filter((page) => {
      // list がない、または list のすべての form_name が空の場合は除外
      if (!page.list || page.list.length === 0) return false;

      // すべての list アイテムに有効な fields があるかチェック
      const hasValidFields = page.list.some(
        (listItem) =>
          listItem.fields &&
          listItem.fields.length > 0 &&
          (listItem.form_name || listItem.fields.some(f => f.name))
      );

      return hasValidFields;
    });

    return {
      ...formProps,
      page: filteredPages
    };
  }, [formProps]);

  // すべてのページのフィールドをフラット化
  const allFields = React.useMemo(() => {
    const fields: Record<string, any> = {};
    filteredFormProps.page.forEach((page) => {
      page.list.forEach((item) => {
        item.fields.forEach((field) => {
          fields[field.name] = field;
        });
      });
    });
    return fields;
  }, [filteredFormProps]);

  // フォーム送信処理
  const handleFormSubmit = React.useCallback(
    async (_data: Record<string, unknown>) => {
      if (step < filteredFormProps.page.length - 1) {
        // 次のステップへ
        setStep((prev) => prev + 1);
      } else {
        // 最終ステップ：すべてのsessionStorageデータを収集して送信
        try {
          const allData: Record<string, unknown> = {};
          Object.keys(allFields).forEach((fieldName) => {
            const savedValue = getFormInput(fieldName);
            if (savedValue) {
              const field = allFields[fieldName];
              if (field.type === "picker") {
                try {
                  allData[fieldName] = JSON.parse(savedValue);
                } catch {
                  allData[fieldName] = savedValue;
                }
              } else {
                allData[fieldName] = savedValue;
              }
            }
          });

          await callback(allData);
        } catch (error) {
          console.error("[DynamicMultiStepForm] フォーム送信エラー:", error);
        }
      }
    },
    [step, filteredFormProps.page.length, callback, allFields]
  );

  // 戻るボタン処理
  const handlePrev = React.useCallback(() => {
    if (step === 0) {
      // CTAページに戻る
      openBottomSheet("CTA");
    } else {
      setStep((prev) => prev - 1);
    }
  }, [step, openBottomSheet]);

  // step変更時にコンポーネントを完全に再マウント
  // これによりreact-hook-formのrefが完全にクリーンアップされる
  const isLastStep = step === filteredFormProps.page.length - 1;

  return (
    <StepFormInner
      key={`step-${step}`}
      step={step}
      formProps={filteredFormProps}
      onSubmit={handleFormSubmit}
      onPrev={handlePrev}
      isLastStep={isLastStep}
    />
  );
}
